/**
 * Shared Recharge selling-plan helpers.
 *
 * Frequency and quantity come from the options Recharge attaches to every
 * selling plan, not from the plan name:
 *     "Order Frequency and Unit": "4-week"
 *     "Product Quantity": "1"
 * so the name is free to be whatever reads best in cart and checkout
 * ("10% off first order then delivered every 4 weeks"). The name is only parsed
 * for whatever the options leave out, in one of two shapes:
 *
 *   Quantity upsell — the discount varies with how many you buy, so the
 *   quantity is named:
 *     "{qty} product{s} every {frequency} {unit} at {discount}% discount"
 *     e.g. "1 product every month at 10% discount"
 *          "2 products every 3 weeks at 15% discount"
 *
 *   Flat — one saving whatever the quantity, so there is no quantity to name:
 *     "every {frequency} {unit} at {discount}% discount"
 *     e.g. "Every month at 25% discount"
 *
 * A flat plan parses with `quantity: null` and is bucketed as `anyQtyPlan`, a
 * wildcard covering every quantity. Use planForQuantity() rather than reaching
 * into plansByQty directly, so both shapes resolve.
 *
 * Used by both the PDP/card subscription widget and the cart-drawer
 * "Upgrade to subscription" control so parsing stays consistent.
 */
(function () {
  const PLAN_NAME_RE =
    /^(?:(\d+)\s+products?\s+)?every\s+(?:(\d+)\s+)?(day|days|week|weeks|month|months|year|years)(?:\s+at\s+(\d+(?:\.\d+)?)%\s+discount)?/i;

  /** Parse a plan name into structured data, or null. */
  function parsePlanName(name) {
    if (!name || typeof name !== 'string') return null;
    const m = name.trim().match(PLAN_NAME_RE);
    if (!m) return null;
    const qty = m[1] ? parseInt(m[1], 10) : null;
    const freq = m[2] ? parseInt(m[2], 10) : 1;
    let unit = m[3].toLowerCase();
    if (unit.endsWith('s')) unit = unit.slice(0, -1);
    const discount = m[4] ? parseFloat(m[4]) : null;
    return {
      quantity: qty,
      intervalFrequency: freq,
      intervalUnit: unit,
      discountPercent: discount,
    };
  }

  const FREQUENCY_OPTION = 'order frequency and unit';
  const QUANTITY_OPTION = 'product quantity';
  const FREQUENCY_VALUE_RE = /^(\d+)[-\s]*(day|week|month|year)s?$/i;

  function optionValue(plan, optionName) {
    const opt = ((plan && plan.options) || []).find(
      (o) => o && typeof o.name === 'string' && o.name.trim().toLowerCase() === optionName
    );
    return opt && opt.value != null ? String(opt.value).trim() : '';
  }

  /**
   * Parse a selling plan into structured data, or null.
   *
   * Recharge's options win; the name fills in anything they don't give. The two
   * can disagree — plans named "every month" are really "4-week" — and the
   * option is what Recharge ships on, so that is what the dropdown says.
   */
  function parsePlan(plan) {
    if (!plan) return null;
    const parsed = parsePlanName(plan.name) || {
      quantity: null,
      intervalFrequency: null,
      intervalUnit: null,
      discountPercent: null,
    };
    const freq = optionValue(plan, FREQUENCY_OPTION).match(FREQUENCY_VALUE_RE);
    if (freq) {
      parsed.intervalFrequency = parseInt(freq[1], 10);
      parsed.intervalUnit = freq[2].toLowerCase();
    }
    const qty = parseInt(optionValue(plan, QUANTITY_OPTION), 10);
    if (qty > 0) parsed.quantity = qty;
    return parsed.intervalUnit ? parsed : null;
  }

  /**
   * Resolve a discount percent: prefer the price adjustment, fall back to the
   * parsed name.
   *
   * The adjustment is what Shopify actually charges. Plan names are merchant
   * copy and may quote a headline figure that bundles other discounts, so they
   * cannot be trusted for pricing maths — only as a fallback when no percentage
   * adjustment is present.
   */
  function extractDiscountFromPlan(plan, parsed) {
    const adj = plan && plan.price_adjustments && plan.price_adjustments[0];
    if (adj && adj.value_type === 'percentage') {
      const value = parseFloat(adj.value);
      if (!isNaN(value)) return value;
    }
    if (parsed && typeof parsed.discountPercent === 'number' && !isNaN(parsed.discountPercent)) {
      return parsed.discountPercent;
    }
    return 0;
  }

  function frequencyLabel(unit, freq) {
    const unitPlural = freq === 1 ? unit : `${unit}s`;
    if (freq === 1) return `Every ${unit}`;
    return `Every ${freq} ${unitPlural}`;
  }

  /** Approximate number of days between deliveries. Informational only — see buildFrequencies. */
  function frequencySortKey(unit, freq) {
    const days = { day: 1, week: 7, month: 30, year: 365 }[unit] || 30;
    return days * freq;
  }

  /**
   * Build frequency buckets from a flat list of selling plans.
   *
   * Order is the order Shopify returns the selling plans in, which mirrors how
   * they are arranged in Recharge — so merchandising the dropdown is done in
   * Recharge, not here. A frequency takes the position of the FIRST plan that
   * uses it (plans are bucketed across quantities).
   *
   * Returns an array of:
   *   { key, intervalUnit, intervalFrequency, label, sortKey, sourceIndex,
   *     plansByQty: { [qty]: { id, discountPercent, raw } },
   *     anyQtyPlan: { id, discountPercent, raw } | null }
   */
  function buildFrequencies(plans) {
    const buckets = new Map();
    (plans || []).forEach((plan, index) => {
      const parsed = parsePlan(plan);
      if (!parsed) return;
      const key = `${parsed.intervalUnit}:${parsed.intervalFrequency}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          key,
          intervalUnit: parsed.intervalUnit,
          intervalFrequency: parsed.intervalFrequency,
          label: frequencyLabel(parsed.intervalUnit, parsed.intervalFrequency),
          sortKey: frequencySortKey(parsed.intervalUnit, parsed.intervalFrequency),
          sourceIndex: index,
          plansByQty: {},
          anyQtyPlan: null,
        });
      }
      const entry = {
        id: plan.id,
        discountPercent: extractDiscountFromPlan(plan, parsed),
        raw: plan,
      };
      if (parsed.quantity === null) {
        buckets.get(key).anyQtyPlan = entry;
      } else {
        buckets.get(key).plansByQty[parsed.quantity] = entry;
      }
    });
    // Map preserves insertion order — no sort, Recharge's order wins.
    return Array.from(buckets.values());
  }

  /**
   * The plan covering `qty` in a frequency bucket, or null.
   *
   * A quantity-specific plan wins over the wildcard, so a store can run flat
   * pricing with a per-quantity exception without the two fighting.
   */
  function planForQuantity(bucket, qty) {
    if (!bucket) return null;
    return (bucket.plansByQty && bucket.plansByQty[qty]) || bucket.anyQtyPlan || null;
  }

  /** Quantities a bucket prices explicitly. Empty for a flat (wildcard) plan. */
  function quantitiesFor(bucket) {
    if (!bucket || !bucket.plansByQty) return [];
    return Object.keys(bucket.plansByQty).map(Number);
  }

  window.SubscriptionPlans = {
    parsePlanName,
    parsePlan,
    extractDiscountFromPlan,
    frequencyLabel,
    frequencySortKey,
    buildFrequencies,
    planForQuantity,
    quantitiesFor,
  };
})();
